import React, { useState } from 'react';
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
} from 'firebase/auth';
import { useNavigate } from 'react-router-dom';
import { auth } from '../firebaseConfig';
import { registerCustomer } from '../lib/callables';
import './Signup.css';

const ROLES = ['Founder', 'CEO', 'CTO', 'Engineer', 'ML Engineer', 'Researcher', 'Product Manager', 'Student', 'Other'];
const WORKING_ON = ['Robotics', 'Physical AI', 'OCR', 'Vision Models', 'Food Recognition', 'Research', 'Other'];
const DATA_TYPES = [
  'Robotics / Physical AI', 'Household Activities', 'Object Detection', 'OCR / Handwriting',
  'Segmentation', 'Food & Agriculture', 'Video Datasets', 'Annotated Images', 'Raw Images',
  'Synthetic Data', 'Model Evaluation Data', 'Other',
];
const TIMELINES = ['Just Exploring', 'This Quarter', 'Within 30 Days', 'Need Custom Dataset'];
const COMPANY_SIZES = ['1', '2–10', '11–50', '51–200', '200+'];
const HEARD_ABOUT = ['Google', 'X', 'Discord', 'Cardano', 'Rare Evo', 'Referral', 'Other'];

export default function Signup() {
  const navigate = useNavigate();

  const [hasContributor, setHasContributor] = useState<boolean | null>(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  const [name, setName] = useState('');
  const [company, setCompany] = useState('');
  const [role, setRole] = useState('');
  const [workingOn, setWorkingOn] = useState('');
  const [dataTypes, setDataTypes] = useState<Set<string>>(new Set());
  const [dataTypesOther, setDataTypesOther] = useState('');
  const [timeline, setTimeline] = useState('');
  const [website, setWebsite] = useState('');
  const [companySize, setCompanySize] = useState('');
  const [heardAbout, setHeardAbout] = useState('');
  const [wantsConsult, setWantsConsult] = useState<boolean | null>(null);

  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const toggleDataType = (dt: string) => {
    setDataTypes((prev) => {
      const next = new Set(prev);
      if (next.has(dt)) next.delete(dt);
      else next.add(dt);
      return next;
    });
  };

  const validate = (): string | null => {
    if (hasContributor === null) return 'Please tell us whether you already have a Pixano mobile account.';
    if (!email.trim()) return 'Email is required.';
    if (!password) return 'Password is required.';
    if (!name.trim()) return 'Name is required.';
    if (!company.trim()) return 'Company / Organization is required.';
    if (!role) return 'Please select your role.';
    if (!workingOn) return 'Please tell us what you are working on.';
    if (dataTypes.size === 0) return 'Please select at least one type of data you are looking for.';
    if (dataTypes.has('Other') && !dataTypesOther.trim()) return 'Please describe the "Other" data you are looking for.';
    if (!timeline) return 'Please select your timeline.';
    return null;
  };

  const handleSubmit = async () => {
    setError('');
    const problem = validate();
    if (problem) {
      setError(problem);
      return;
    }
    setLoading(true);
    try {
      // Establish the account. "Yes" branch signs into an existing mobile
      // account; "No" branch creates a fresh one. Either way we end up signed in.
      if (hasContributor) {
        try {
          await signInWithEmailAndPassword(auth, email.trim(), password);
        } catch (e: any) {
          setError('Could not sign in with those credentials. Check your email and password, or choose "No" to create a new account.');
          setLoading(false);
          return;
        }
      } else {
        try {
          await createUserWithEmailAndPassword(auth, email.trim(), password);
        } catch (e: any) {
          if (e?.code === 'auth/email-already-in-use') {
            setError('An account with this email already exists. If it is your Pixano mobile account, choose "Yes" above and sign in with it.');
          } else if (e?.code === 'auth/weak-password') {
            setError('Password should be at least 6 characters.');
          } else {
            setError(e?.message || 'Could not create your account.');
          }
          setLoading(false);
          return;
        }
      }

      // Grant the customer claim + write profile + save intake (backend).
      await registerCustomer({
        name: name.trim(),
        company: company.trim(),
        role,
        workingOn,
        dataTypes: Array.from(dataTypes),
        dataTypesOther: dataTypesOther.trim(),
        timeline,
        website: website.trim(),
        companySize,
        heardAbout,
        wantsConsult: wantsConsult === true,
        hadContributorAccount: hasContributor === true,
      });

      // Force a token refresh so the new customer claim is live immediately,
      // without requiring the user to log out and back in.
      if (auth.currentUser) {
        await auth.currentUser.getIdToken(true);
      }

      navigate('/dashboard');
    } catch (e: any) {
      // If registration failed after the account was established, do not leave
      // a half-open session behind.
      try { await signOut(auth); } catch { /* ignore */ }
      setError(e?.message || 'Something went wrong completing your registration. Please try again.');
      setLoading(false);
    }
  };

  return (
    <div className="signup-bg">
      <div className="signup-container">
        <img src="/pix-logo.png" alt="Pixano Logo" className="signup-logo" />
        <div className="signup-title">Create a Customer Account</div>
        <div className="signup-subtitle">Access the Pixano dataset marketplace.</div>

        {error && <div className="signup-error">{error}</div>}

        <div className="signup-section-label">Getting Started</div>
        <label className="signup-field-label">Do you already have a Pixano contributor account on the mobile app?</label>
        <div className="signup-branch-row">
          <button
            type="button"
            className={`signup-branch-btn ${hasContributor === true ? 'active' : ''}`}
            onClick={() => setHasContributor(true)}
          >
            Yes
          </button>
          <button
            type="button"
            className={`signup-branch-btn ${hasContributor === false ? 'active' : ''}`}
            onClick={() => setHasContributor(false)}
          >
            No
          </button>
        </div>

        <label className="signup-field-label">Email</label>
        <input className="signup-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
          placeholder={hasContributor ? 'Your existing mobile account email' : 'you@company.com'} />

        <label className="signup-field-label">Password</label>
        <input className="signup-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
          placeholder={hasContributor ? 'Your existing mobile account password' : 'Create a password'} />

        <div className="signup-section-label">Required</div>

        <label className="signup-field-label">Name</label>
        <input className="signup-input" type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="Your full name" />

        <label className="signup-field-label">Company / Organization</label>
        <input className="signup-input" type="text" value={company} onChange={(e) => setCompany(e.target.value)} placeholder="Company or organization" />

        <label className="signup-field-label">Role</label>
        <select className="signup-select" value={role} onChange={(e) => setRole(e.target.value)}>
          <option value="">Select your role…</option>
          {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
        </select>

        <label className="signup-field-label">What are you working on?</label>
        <select className="signup-select" value={workingOn} onChange={(e) => setWorkingOn(e.target.value)}>
          <option value="">Select…</option>
          {WORKING_ON.map((w) => <option key={w} value={w}>{w}</option>)}
        </select>

        <label className="signup-field-label">What kind of data are you looking for?</label>
        <div className="signup-checkbox-grid">
          {DATA_TYPES.map((dt) => (
            <label key={dt} className="signup-checkbox-row">
              <input type="checkbox" checked={dataTypes.has(dt)} onChange={() => toggleDataType(dt)} />
              {dt}
            </label>
          ))}
        </div>
        {dataTypes.has('Other') && (
          <input className="signup-input" type="text" value={dataTypesOther} onChange={(e) => setDataTypesOther(e.target.value)}
            placeholder="Tell us what you're looking for…" />
        )}

        <label className="signup-field-label">Timeline</label>
        <select className="signup-select" value={timeline} onChange={(e) => setTimeline(e.target.value)}>
          <option value="">Select…</option>
          {TIMELINES.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>

        <div className="signup-section-label">Optional</div>

        <label className="signup-field-label">Company Website</label>
        <input className="signup-input" type="text" value={website} onChange={(e) => setWebsite(e.target.value)} placeholder="https://company.com" />

        <label className="signup-field-label">Company Size</label>
        <select className="signup-select" value={companySize} onChange={(e) => setCompanySize(e.target.value)}>
          <option value="">Select…</option>
          {COMPANY_SIZES.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>

        <label className="signup-field-label">How did you hear about Pixano?</label>
        <select className="signup-select" value={heardAbout} onChange={(e) => setHeardAbout(e.target.value)}>
          <option value="">Select…</option>
          {HEARD_ABOUT.map((h) => <option key={h} value={h}>{h}</option>)}
        </select>

        <label className="signup-field-label">Would you like a free dataset consultation call?</label>
        <div className="signup-branch-row">
          <button type="button" className={`signup-branch-btn ${wantsConsult === true ? 'active' : ''}`} onClick={() => setWantsConsult(true)}>Yes</button>
          <button type="button" className={`signup-branch-btn ${wantsConsult === false ? 'active' : ''}`} onClick={() => setWantsConsult(false)}>No</button>
        </div>

        <button className="signup-btn" onClick={handleSubmit} disabled={loading}>
          {loading ? 'Creating your account…' : 'Create Customer Account'}
        </button>

        <div className="signup-footer">
          Already have a customer account?{' '}
          <button className="signup-link" onClick={() => navigate('/login')}>Log in</button>
        </div>
      </div>
    </div>
  );
}
